import { ChartColumn, ChartPie } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type PieLabelRenderProps,
} from 'recharts'

import { cn } from '@/lib/utils'
import { Button } from '@/shared/components'

export type ReportCategoryChartType = 'pie' | 'bar'

export type ReportCategoryChartRow = {
  key: string
  label: string
  count: number
  colour?: string
}

type ReportCategoryChartProps = {
  rows: ReportCategoryChartRow[]
  chartType: ReportCategoryChartType
  onChartTypeChange: (chartType: ReportCategoryChartType) => void
  onCategoryClick?: (key: string) => void
  emptyMessage?: string
}

type ChartDatum = {
  key: string
  name: string
  value: number
  colour?: string
}

const CHART_COLOURS = [
  '#174194',
  '#1d9dd8',
  '#e04613',
  '#168039',
  '#0b2c6f',
  '#6590e7',
]

const PIE_GAP_STROKE = 'var(--color-surface-tint)'
const PIE_GAP_WIDTH = 4

const RADIAN = Math.PI / 180
const LABEL_OFFSET = 36

const createPieLabelRenderer = (showCount: boolean) => {
  return ({
    cx = 0,
    cy = 0,
    midAngle = 0,
    outerRadius = 0,
    name,
    percent = 0,
    value,
  }: PieLabelRenderProps) => {
    if (!Number(value)) {
      return null
    }

    const radius = Number(outerRadius) + LABEL_OFFSET
    const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN)
    const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN)
    const detail = showCount
      ? String(value ?? 0)
      : `${(percent * 100).toFixed(0)}%`

    return (
      <text
        x={x}
        y={y}
        textAnchor={x > Number(cx) ? 'start' : 'end'}
        dominantBaseline="central"
        className="fill-foreground text-xs"
      >
        {`${name} (${detail})`}
      </text>
    )
  }
}

const resolveCategoryKey = (
  entry: ChartDatum | { payload?: ChartDatum } | undefined
) => {
  if (!entry) {
    return undefined
  }

  if ('key' in entry) {
    return entry.key
  }

  return entry.payload?.key
}

const CategoryCountList = ({ rows }: { rows: ReportCategoryChartRow[] }) => (
  <ul
    aria-label="Category counts"
    className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-foreground"
  >
    {rows.map((row) => (
      <li key={row.key}>{`${row.label}: ${row.count}`}</li>
    ))}
  </ul>
)

const ChartTypeToggle = ({
  chartType,
  onChange,
}: {
  chartType: ReportCategoryChartType
  onChange: (chartType: ReportCategoryChartType) => void
}) => (
  <div
    className="inline-flex gap-1 rounded-button bg-surface-muted p-1"
    role="group"
    aria-label="Chart type"
  >
    <Button
      type="button"
      size="sm"
      variant={chartType === 'pie' ? 'primary-dark' : 'secondary-light'}
      className={cn('px-3 py-2', chartType !== 'pie' && 'bg-transparent')}
      aria-pressed={chartType === 'pie'}
      onClick={() => onChange('pie')}
    >
      <ChartPie className="size-4" aria-hidden />
      <span>Pie</span>
    </Button>
    <Button
      type="button"
      size="sm"
      variant={chartType === 'bar' ? 'primary-dark' : 'secondary-light'}
      className={cn('px-3 py-2', chartType !== 'bar' && 'bg-transparent')}
      aria-pressed={chartType === 'bar'}
      onClick={() => onChange('bar')}
    >
      <ChartColumn className="size-4" aria-hidden />
      <span>Bar</span>
    </Button>
  </div>
)

const CategoryPieView = ({
  data,
  isClickable,
  onCategoryClick,
}: {
  data: ChartDatum[]
  isClickable: boolean
  onCategoryClick?: (key: string) => void
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <PieChart margin={{ top: 40, right: 120, bottom: 24, left: 120 }}>
      <Pie
        data={data}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="48%"
        outerRadius={110}
        stroke={PIE_GAP_STROKE}
        strokeWidth={PIE_GAP_WIDTH}
        activeShape={false}
        label={createPieLabelRenderer(data.length === 1)}
        labelLine={{
          stroke: '#585858',
          strokeWidth: 1,
          length: 18,
          length2: LABEL_OFFSET - 10,
        }}
        className={isClickable ? 'cursor-pointer' : undefined}
        onClick={
          isClickable
            ? (entry) => {
                const key = resolveCategoryKey(
                  entry as ChartDatum | { payload?: ChartDatum } | undefined
                )
                if (key) {
                  onCategoryClick?.(key)
                }
              }
            : undefined
        }
      >
        {data.map((entry, index) => (
          <Cell
            key={entry.key}
            fill={entry.colour ?? CHART_COLOURS[index % CHART_COLOURS.length]}
            stroke={PIE_GAP_STROKE}
            strokeWidth={PIE_GAP_WIDTH}
          />
        ))}
      </Pie>
      <Tooltip />
      <Legend
        verticalAlign="bottom"
        align="center"
        wrapperStyle={{ paddingTop: 16 }}
      />
    </PieChart>
  </ResponsiveContainer>
)

const CategoryBarView = ({
  data,
  isClickable,
  onCategoryClick,
}: {
  data: ChartDatum[]
  isClickable: boolean
  onCategoryClick?: (key: string) => void
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 16, right: 24, bottom: 64, left: 16 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#cccccc" />
      <XAxis
        dataKey="name"
        interval={0}
        angle={-25}
        textAnchor="end"
        height={70}
        tick={{ fontSize: 12, fill: '#313131' }}
      />
      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#313131' }} />
      <Tooltip />
      <Bar
        dataKey="value"
        name="Count"
        className={isClickable ? 'cursor-pointer' : undefined}
        onClick={
          isClickable
            ? (entry) => {
                const key = resolveCategoryKey(
                  entry as ChartDatum | { payload?: ChartDatum } | undefined
                )
                if (key) {
                  onCategoryClick?.(key)
                }
              }
            : undefined
        }
      >
        {data.map((entry, index) => (
          <Cell
            key={entry.key}
            fill={entry.colour ?? CHART_COLOURS[index % CHART_COLOURS.length]}
            stroke="none"
          />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
)

export const ReportCategoryChart = ({
  rows,
  chartType,
  onChartTypeChange,
  onCategoryClick,
  emptyMessage = 'No results match the current filters.',
}: ReportCategoryChartProps) => {
  if (rows.length === 0) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-3">
        <ChartTypeToggle chartType={chartType} onChange={onChartTypeChange} />
        <p className="text-center text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    )
  }

  const data: ChartDatum[] = rows.map((row) => ({
    key: row.key,
    name: row.label,
    value: row.count,
    colour: row.colour,
  }))
  const pieData = data.filter((entry) => entry.value > 0)

  if (chartType === 'pie' && pieData.length === 0) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-3">
        <ChartTypeToggle chartType={chartType} onChange={onChartTypeChange} />
        <CategoryCountList rows={rows} />
        <p className="text-center text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    )
  }

  const isClickable =
    Boolean(onCategoryClick) &&
    (chartType === 'pie' ? pieData.length > 1 : data.length > 1)

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-3">
      <ChartTypeToggle chartType={chartType} onChange={onChartTypeChange} />
      <CategoryCountList rows={rows} />

      <div className="h-[28rem] w-full [&_path]:outline-none [&_*]:outline-none">
        {chartType === 'pie' ? (
          <CategoryPieView
            data={pieData}
            isClickable={isClickable}
            onCategoryClick={onCategoryClick}
          />
        ) : (
          <CategoryBarView
            data={data}
            isClickable={isClickable}
            onCategoryClick={onCategoryClick}
          />
        )}
      </div>
    </div>
  )
}
